import type { ContentBlockType, File } from "@prisma/client";
import Dialog from "@reach/dialog";
import { IKImageProps } from "imagekitio-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFetcher } from "remix";
import { ContentBlockActionData } from "~/routes/admin/content-block";
import {
  ContentBlockModel,
  isNewContentBlock,
  LoadedContentBlock,
} from "~/utils/contentBlocks";
import { contentBlockTypeOrNull } from "~/utils/validate";
import { Button, EditButton } from "./Button";
import { FormInput, FormSelect } from "./FormInput";
import { ImageComponent } from "./ImageComponent";
import { Tiptap } from "./TipTap";
import { UploadDialog } from "./UploadDialog";

export interface ContentBlockOwnProps {
  editable?: boolean;
  block: ContentBlockModel;
  imageProps?: Omit<IKImageProps, "path">;
  className?: string;
}

export function ContentBlock({
  editable,
  block,
  imageProps,
  className,
}: ContentBlockOwnProps) {
  const [editMode, setEditMode] = useState(false);

  const closeEditMode = useCallback(() => setEditMode(false), []);

  return (
    <>
      {isNewContentBlock(block) ? (
        "Empty Block"
      ) : (
        <ContentBlockView
          className={className}
          block={block}
          imageProps={imageProps}
        />
      )}
      {editable && (
        <EditButton onClick={() => setEditMode(true)}>
          {editMode ? "Editing…" : "Edit this"}
        </EditButton>
      )}
      {editable && editMode && (
        <Dialog
          isOpen
          onDismiss={closeEditMode}
          aria-label="Edit Content Block"
        >
          <ContentBlockForm block={block} onRequestClose={closeEditMode} />
          {editable && <EditButton onClick={closeEditMode}>Cancel</EditButton>}
        </Dialog>
      )}
    </>
  );
}

export interface ContentBlockViewOwnProps {
  block: LoadedContentBlock;
  className?: string;
  imageProps?: Omit<IKImageProps, "path">;
}

export function ContentBlockView({
  block,
  imageProps,
  className,
}: ContentBlockViewOwnProps) {
  switch (block.type) {
    case "Html":
      return (
        <div
          className={"prose-sm md:prose" + (className ? " " + className : "")}
          dangerouslySetInnerHTML={{ __html: block.content ?? "" }}
        />
      );
    case "Image":
      if (block.image) {
        return <ImageComponent image={block.image} {...imageProps} />;
      } else {
        return <p>No image connected</p>;
      }
    default:
      return <>{block.content}</>;
  }
}

export interface ContentBlockFormOwnProps {
  block: Partial<LoadedContentBlock>;
  onRequestClose?: () => void;
}

export function ContentBlockForm({
  block,
  onRequestClose,
}: ContentBlockFormOwnProps) {
  const blockFetcher = useFetcher<ContentBlockActionData>();
  const ref = useRef<HTMLFormElement>(null);
  const [currentType, setCurrentType] = useState<ContentBlockType | undefined>(
    blockFetcher.data?.fields?.type || block.type
  );
  const [file, setFile] = useState<File | null>(block?.image ?? null);

  useEffect(() => {
    if (blockFetcher.type === "done" && blockFetcher.data.ok) {
      ref.current?.reset();
      onRequestClose?.();
    }
  });

  return (
    <blockFetcher.Form
      ref={ref}
      method="post"
      action="/admin/content-block"
      className="p-2 border-2"
    >
      <h3 className="text-bold text-sm text-primary-500">
        Editing Block: {block.slug}
      </h3>
      {!!blockFetcher.data?.formError && (
        <p className="text-red-500">{blockFetcher.data.formError}</p>
      )}
      <input
        name="slug"
        type="hidden"
        value={blockFetcher.data?.fields?.slug?.toString() || block.slug}
      />
      <FormSelect
        onChange={(e) => {
          const newType = contentBlockTypeOrNull(e.target.value);

          if (newType) {
            setCurrentType(newType);
          }
        }}
        label="Type"
        name="type"
        defaultValue={blockFetcher.data?.fields?.type?.toString() || block.type}
        fieldError={blockFetcher.data?.fieldErrors?.type?.[0]}
      >
        <option label="Please Select" />
        <option label="Text" value="Text" />
        <option label="Image" value="Image" />
        <option label="Html" value="Html" />
      </FormSelect>
      {currentType === "Text" && (
        <FormInput
          label="Content"
          name="content"
          defaultValue={
            blockFetcher.data?.fields?.content?.toString() ||
            block.content ||
            ""
          }
          fieldError={blockFetcher.data?.fieldErrors?.content?.[0]}
        />
      )}
      {currentType === "Html" && (
        <Tiptap
          label="Content"
          name="content"
          defaultValue={
            blockFetcher.data?.fields?.content?.toString() ||
            block.content ||
            ""
          }
        />
      )}

      {currentType === "Image" && (
        <UploadDialog
          label="Image"
          name="imageId"
          value={file?.id}
          file={file}
          onFileCreated={(newFile) => setFile(newFile)}
        />
      )}

      <Button type="submit" disabled={blockFetcher.state === "submitting"}>
        Save
      </Button>
    </blockFetcher.Form>
  );
}
